/*
 * DRAFT — requires solicitor review before real customers sign up.
 *
 * Terms.tsx — the public /terms page (LegalController#terms → marketing/Terms).
 *
 * An editorial legal page in the landing3 (mkt3-*) design language: warm-stone paper,
 * mkt3-eyebrow / mkt3-display heading, mono section numerals, a max-w-prose body with
 * generous rhythm, wrapped in Marketing3Layout so it carries the same nav + footer + brand
 * glyph as the landing. Reveal3 gives a subtle, reduced-motion-safe entrance.
 *
 * Every string honours the §0 do-not-publish list (docs/marketing/claims-register.md): no
 * PMS name ("your property management system" / "your PMS"), no stage counts, no compute
 * cadence, no sync-depth number, no verification claims, no competitor names, no reply-time
 * promises, no unguaranteed "no long-term contract" line. The brand comes from the `brand`
 * prop; contact + the "rates stay in your PMS on exit" idea are the only registered specifics.
 */
import React from 'react'
import './marketing3.css'
import Marketing3Layout from '@/components/marketing3/Marketing3Layout'
import Reveal3 from '@/components/marketing3/Reveal3'
import { FALLBACK_BRAND, LEGAL_NAME, SUPPORT_EMAIL } from '@/lib/brand'

interface TermsProps {
  brand?: string | null
}

interface Clause {
  n: string
  title: string
  body: React.ReactNode
}

export default function Terms({ brand: brandProp }: TermsProps) {
  // Strict string guard (parity with Landing3): a misconfigured server can send a truthy
  // non-string — fall back rather than blank-paging a legal page.
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND
  const contact = SUPPORT_EMAIL

  const clauses: Clause[] = [
    {
      n: '01',
      title: 'The service',
      body: (
        <>
          <p className="mkt3-prose">
            {brand} is revenue-management software for hotels. It connects to your property management system, reads
            your rooms, rate plans and booking activity, and computes recommended room rates through a deterministic
            engine that shows its working. You choose when — and whether — those rates are published back to your
            property management system.
          </p>
          <p className="mkt3-prose">
            These Terms govern your access to and use of {brand} (the &ldquo;Service&rdquo;), operated by {LEGAL_NAME},
            a company registered in England and Wales. By creating an account or using the Service, you agree to them.
            If you are agreeing on behalf of a business, you confirm you have authority to bind that business.
          </p>
        </>
      ),
    },
    {
      n: '02',
      title: 'Your account',
      body: (
        <>
          <p className="mkt3-prose">
            Access to {brand} is by invitation. You are responsible for keeping your login credentials confidential and
            for all activity under your account. Tell us promptly if you believe your credentials have been compromised.
          </p>
          <p className="mkt3-prose">
            You must give accurate account and connection details and keep them current. Each login is personal to a
            named individual; you may invite additional team members and set their level of access.
          </p>
        </>
      ),
    },
    {
      n: '03',
      title: 'Acceptable use',
      body: (
        <>
          <p className="mkt3-prose">You agree not to:</p>
          <ul className="mkt3-prose mt-2 list-disc space-y-1.5 pl-5">
            <li>use the Service unlawfully, or to infringe the rights of others;</li>
            <li>attempt to access, probe or disrupt the Service, its infrastructure or other customers&rsquo; data;</li>
            <li>reverse-engineer, copy or resell the Service except where the law expressly permits;</li>
            <li>upload material you have no right to share, or that is unlawful or harmful; or</li>
            <li>use the Service to send unsolicited communications.</li>
          </ul>
          <p className="mkt3-prose mt-4">
            You remain responsible for the rates you choose to publish and for compliance with your own commercial and
            regulatory obligations.
          </p>
        </>
      ),
    },
    {
      n: '04',
      title: 'Fees and billing',
      body: (
        <>
          <p className="mkt3-prose">
            Access to the Service is provided for the fees set out in your order or agreed with us in writing. Unless
            stated otherwise, fees are billed in advance, are exclusive of VAT and any applicable taxes, and are payable
            by the method on file for your account.
          </p>
          <p className="mkt3-prose">
            We may change our fees on reasonable notice; any change takes effect at the start of your next billing
            period. Fees already paid are non-refundable except where required by law or expressly stated in your order.
          </p>
        </>
      ),
    },
    {
      n: '05',
      title: 'Intellectual property',
      body: (
        <p className="mkt3-prose">
          The Service, and all software, models, designs and content that make it up, belong to {brand} and its
          licensors. We grant you a non-exclusive, non-transferable right to use the Service for your own hotel business
          for as long as your account is active. You keep ownership of the data you provide; you grant us the rights we
          need to operate the Service for you, as described in our{' '}
          <a href="/privacy" className="font-medium text-teal-700 underline-offset-2 hover:underline">
            Privacy Policy
          </a>
          .
        </p>
      ),
    },
    {
      n: '06',
      title: 'Availability and changes',
      body: (
        <p className="mkt3-prose">
          We work to keep the Service available and dependable, but we provide it &ldquo;as is&rdquo; and do not
          guarantee it will be uninterrupted or error-free. We may update, improve or change features over time. Where a
          change materially reduces core functionality you rely on, we will give you reasonable notice.
        </p>
      ),
    },
    {
      n: '07',
      title: 'Limitation of liability',
      body: (
        <>
          <p className="mkt3-prose">
            {brand} recommends rates; you decide what to publish. To the fullest extent permitted by law, we are not
            liable for lost revenue, lost profits, or indirect or consequential loss arising from your use of the
            Service or from pricing decisions you make with it.
          </p>
          <p className="mkt3-prose">
            Nothing in these Terms limits liability that cannot be limited by law. Subject to that, our total liability
            in connection with the Service is limited to the fees you paid us in the twelve months before the event
            giving rise to the claim.
          </p>
        </>
      ),
    },
    {
      n: '08',
      title: 'Termination and your rates on exit',
      body: (
        <>
          <p className="mkt3-prose">
            You may close your account at any time. We may suspend or end your access if you materially breach these
            Terms or fail to pay fees when due, giving you a reasonable chance to put things right where practicable.
          </p>
          <p className="mkt3-prose">
            Your rates are never held hostage. If you pause or leave, the last rates {brand} wrote remain in your
            property management system, untouched — we do not roll them back or remove them on exit.
          </p>
        </>
      ),
    },
    {
      n: '09',
      title: 'Governing law',
      body: (
        <p className="mkt3-prose">
          These Terms are governed by the laws of England and Wales, and the courts of England and Wales have exclusive
          jurisdiction over any dispute arising from them.
        </p>
      ),
    },
    {
      n: '10',
      title: 'Contact',
      body: (
        <p className="mkt3-prose">
          Questions about these Terms? Write to us at{' '}
          <a href={`mailto:${contact}`} className="font-medium text-teal-700 underline-offset-2 hover:underline">
            {contact}
          </a>
          .
        </p>
      ),
    },
  ]

  return (
    <Marketing3Layout brand={brand}>
      <article className="mx-auto max-w-3xl px-4 py-20 sm:px-6 sm:py-28">
        <Reveal3>
          <p className="mkt3-eyebrow">Legal</p>
          <h1 className="mkt3-display mkt3-h1 mt-4">Terms of Service</h1>
          <p className="mkt3-label mt-5 normal-case tracking-[0.16em] text-slate-500">Last updated: 5 July 2026</p>
          <p className="mkt3-prose mt-6">
            These Terms set out the agreement between you and {brand} for use of our revenue-management software. We
            have kept them plain. If anything is unclear, ask us.
          </p>
        </Reveal3>

        <div className="mt-14 space-y-14">
          {clauses.map((clause, i) => (
            <Reveal3 key={clause.n} delay={Math.min(i, 3) * 60}>
              <section className="border-t border-stone-200/70 pt-8">
                <div className="flex items-baseline gap-4">
                  <span className="mkt3-label text-teal-700">{clause.n}</span>
                  <h2 className="mkt3-display mkt3-h3">{clause.title}</h2>
                </div>
                <div className="mt-4 space-y-4">{clause.body}</div>
              </section>
            </Reveal3>
          ))}
        </div>

        {/* Modest legal cross-link */}
        <Reveal3 delay={120}>
          <div className="mt-16 border-t border-stone-200/70 pt-8">
            <p className="mkt3-label normal-case tracking-[0.16em] text-slate-500">
              See also{' '}
              <a href="/privacy" className="font-medium text-teal-700 underline-offset-2 hover:underline">
                Privacy Policy
              </a>
              {' · '}
              <a href="/" className="font-medium text-teal-700 underline-offset-2 hover:underline">
                Back to {brand}
              </a>
            </p>
          </div>
        </Reveal3>
      </article>
    </Marketing3Layout>
  )
}
